/**
 * Activate Page
 * Token-based account activation from campaign email links
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getAttribution } from '../../lib/attribution';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

interface StudioData {
  id: string;
  name: string;
  city: string;
  state: string;
  zip_code: string;
  address_line1: string;
}

interface ContactData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}

interface ActivationData {
  studio: StudioData;
  contact: ContactData;
}

type PageState = 'loading' | 'invalid' | 'ready' | 'submitting' | 'error';

export default function ActivatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const token = searchParams.get('token');

  const [pageState, setPageState] = useState<PageState>('loading');
  const [activationData, setActivationData] = useState<ActivationData | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch studio data from token on mount
  useEffect(() => {
    if (!token) {
      setPageState('invalid');
      return;
    }

    const fetchActivationData = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/activate?token=${encodeURIComponent(token)}`);
        const result = await response.json();

        if (!response.ok || result.success === false) {
          setPageState('invalid');
          return;
        }

        const data = result.data as ActivationData;
        setActivationData(data);
        setEmail(data.contact.email || '');
        setPageState('ready');
      } catch {
        setPageState('invalid');
      }
    };

    fetchActivationData();
  }, [token]);

  const validateForm = (): boolean => {
    if (!email) {
      setError('Please enter your email address');
      return false;
    }
    if (!password || !confirmPassword) {
      setError('Please fill in all password fields');
      return false;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }
    if (
      !/[A-Z]/.test(password) ||
      !/[a-z]/.test(password) ||
      !/[0-9]/.test(password) ||
      !/[^A-Za-z0-9]/.test(password)
    ) {
      setError('Password must include uppercase, lowercase, number, and special character');
      return false;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;

    setPageState('submitting');

    try {
      // Activate the dormant account
      const response = await fetch(`${API_BASE_URL}/api/auth/activate-dormant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // SDA-267: attach stored campaign attribution out-of-band; the server
        // validates and stamps it (absent/invalid never blocks activation).
        body: JSON.stringify({ token, email, password, attribution: getAttribution() ?? undefined }),
      });

      const result = await response.json();

      if (!response.ok || result.success === false) {
        const message = result.error?.message || 'Activation failed. Please try again.';
        setError(message);
        setPageState('ready');
        return;
      }

      // Auto-login with the new credentials
      try {
        await login(email, password);
        navigate('/dashboard', { replace: true });
      } catch {
        // Login failed after activation — redirect to login page
        navigate('/login', { replace: true });
      }
    } catch {
      setError('Something went wrong. Please try again.');
      setPageState('ready');
    }
  };

  const formatAddress = (studio: StudioData): string => {
    const parts = [studio.city, studio.state].filter(Boolean);
    if (studio.zip_code) parts.push(studio.zip_code);
    return parts.join(', ');
  };

  return (
    <div className="min-h-screen flex flex-col bg-warm-50">
      {/* Header */}
      <header className="py-6 px-4">
        <div className="max-w-7xl mx-auto">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="Stage Door Access" className="h-7" />
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Loading State */}
          {pageState === 'loading' && (
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-warm-100 text-center">
              <svg className="animate-spin h-8 w-8 mx-auto text-primary-600 mb-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <p className="text-warm-600">Loading your activation details...</p>
            </div>
          )}

          {/* Invalid Token State */}
          {pageState === 'invalid' && (
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-warm-100 text-center">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h1 className="text-2xl font-display font-bold text-warm-900 mb-2">
                Invalid Activation Link
              </h1>
              <p className="text-warm-600 mb-6">
                This activation link is invalid or has already been used.
              </p>
              <div className="flex flex-col gap-3">
                <Link to="/register" className="btn-primary w-full py-3 text-center">
                  Create a New Account
                </Link>
                <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium text-sm">
                  Already have an account? Sign in
                </Link>
              </div>
            </div>
          )}

          {/* Form Ready / Submitting / Error States */}
          {(pageState === 'ready' || pageState === 'submitting' || pageState === 'error') && activationData && (
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-warm-100">
              <div className="text-center mb-6">
                <h1 className="text-2xl font-display font-bold text-warm-900 mb-2">
                  Welcome to Stage Door Access!
                </h1>
                <p className="text-warm-600">
                  Activate your studio account.
                </p>
              </div>

              {/* Studio Info Card */}
              <div className="mb-6 p-4 bg-warm-50 border border-warm-100 rounded-lg">
                <p className="text-xs font-medium text-warm-500 uppercase tracking-wider mb-2">
                  Your Studio
                </p>
                <p className="font-semibold text-warm-900">
                  {activationData.studio.name}
                </p>
                <p className="text-sm text-warm-600">
                  {formatAddress(activationData.studio)}
                </p>
                {(activationData.contact.first_name || activationData.contact.last_name) && (
                  <p className="text-sm text-warm-600 mt-1">
                    Contact: {activationData.contact.first_name} {activationData.contact.last_name}
                  </p>
                )}
              </div>

              {/* Error Alert */}
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-warm-700 mb-1.5">
                    Email Address *
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(null); }}
                    className="input w-full"
                    placeholder="you@studio.com"
                    autoComplete="email"
                    required
                  />
                </div>

                {/* Password */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-warm-700 mb-1.5">
                    Password *
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); setError(null); }}
                      className="input w-full pr-12"
                      placeholder="At least 8 characters"
                      autoComplete="new-password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-warm-500 hover:text-warm-700"
                    >
                      {showPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-warm-500">
                    Must include uppercase, lowercase, number, and special character
                  </p>
                </div>

                {/* Confirm Password */}
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-warm-700 mb-1.5">
                    Confirm Password *
                  </label>
                  <input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); setError(null); }}
                    className="input w-full"
                    placeholder="Re-enter your password"
                    autoComplete="new-password"
                    required
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={pageState === 'submitting'}
                  className="btn-primary w-full min-h-[44px] py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {pageState === 'submitting' ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Activating Account...
                    </span>
                  ) : (
                    'Activate Account'
                  )}
                </button>
              </form>

              {/* Login Link */}
              <p className="mt-8 text-center text-warm-600">
                Already have an account?{' '}
                <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
                  Sign in
                </Link>
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 text-center text-warm-500 text-sm">
        © {new Date().getFullYear()} Stage Door Access. All rights reserved.
      </footer>
    </div>
  );
}
